import { useEffect, useMemo, useState } from 'react';
import {
  Building2,
  FileText,
  FolderX,
  Paperclip,
  Trash2,
  Undo2,
  X,
} from 'lucide-react';
import * as Y from 'yjs';
import type { EncryptedPayload } from '@dragonhq/protocol';
import { api, type AttachmentDto, type CustomerDto, type DocumentDto } from '../lib/api';
import { useVault } from '../../../core/lib/vault';
import { useCanAdminister } from '../lib/usePermissions';
import { decryptJson, encryptJson, fromB64, toB64 } from '../../../core/crypto';
import { pullAll } from '../sync';
import type { CustomerPlain, DocumentPlain } from '../sync';
import { DocumentPreviewPane } from './DocumentPreviewPane';
import {
  acquireAttachmentUrl,
  releaseAttachmentUrl,
} from '../lib/attachmentBlobCache';
import { categoryFor, formatSize, iconFor } from '../lib/attachmentTypes';

interface DeletedCustomer {
  id: string;
  payload: EncryptedPayload;
  plain: CustomerPlain | null;
  deletedAt: string;
  createdAt: string;
}
interface DeletedDocument {
  id: string;
  customerId: string;
  payload: EncryptedPayload;
  plain: DocumentPlain | null;
  deletedAt: string;
}
interface AttachmentMeta {
  filename: string;
  mimetype: string;
  title?: string;
  description?: string;
}
interface DeletedAttachment {
  id: string;
  customerId: string;
  metadata: EncryptedPayload;
  meta: AttachmentMeta | null;
  size: number;
  deletedAt: string;
}

type Selection =
  | { type: 'customer'; id: string }
  | { type: 'document'; id: string }
  | { type: 'attachment'; id: string }
  | null;

export function OrphanageModal({ onClose }: { onClose: () => void }) {
  const vaultKey = useVault((s) => s.vaultKey);
  // Editors and viewers can open the orphanage to SEE what's soft-deleted,
  // but only admins can restore. The footer-level restore controls and the
  // cascade checkbox are gated below; the server rejects the restore POSTs
  // either way.
  const canAdminister = useCanAdminister();
  const [customers, setCustomers] = useState<DeletedCustomer[]>([]);
  const [documents, setDocuments] = useState<DeletedDocument[]>([]);
  const [attachments, setAttachments] = useState<DeletedAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selection, setSelection] = useState<Selection>(null);
  // When restoring a customer, also re-surface the children that went
  // down in the same cascade (±2 s window on deletedAt — matches the
  // server's cascade-restore implementation). Default ON so the common
  // path ("I deleted this customer by mistake, give me the whole thing
  // back") works in one click. User can uncheck to bring just the
  // customer record back and fish children out individually.
  const [cascadeChildren, setCascadeChildren] = useState(true);

  async function reload() {
    if (!vaultKey) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.orphanage();
      setCustomers(await decryptCustomers(res.customers, vaultKey));
      setDocuments(await decryptDocuments(res.documents, vaultKey));
      setAttachments(await decryptAttachments(res.attachments, vaultKey));
    } catch (err: any) {
      setError(err?.body?.error ?? err?.message ?? 'Could not load orphanage.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vaultKey]);

  // Map of customerId → count of soft-deleted child pages. Used in the
  // customer preview to flag "restore the customer alone will leave N pages
  // in the orphanage".
  const orphanedChildCount = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of documents) m.set(d.customerId, (m.get(d.customerId) ?? 0) + 1);
    return m;
  }, [documents]);

  // Map of customerId → counts of children deleted **as part of the same
  // cascade** (deletedAt within ±2 s of the parent customer's deletedAt).
  // These are the rows the server will re-surface when `cascadeChildren`
  // is true on restore. Individually-deleted children outside the window
  // stay in the orphanage. Mirrors the server's restore-cascade logic in
  // `apps/vault/routes/documents.ts::POST /api/customers/:id/restore`.
  const customerCascadeChildren = useMemo(() => {
    const CASCADE_WINDOW_MS = 2000;
    const m = new Map<string, { docs: number; attachments: number }>();
    const customerDeletedAt = new Map<string, number>();
    for (const c of customers) customerDeletedAt.set(c.id, Date.parse(c.deletedAt));
    function bump(customerId: string, kind: 'docs' | 'attachments', childDeletedAt: string) {
      const parentAt = customerDeletedAt.get(customerId);
      if (parentAt == null) return;
      const childAt = Date.parse(childDeletedAt);
      if (Math.abs(childAt - parentAt) > CASCADE_WINDOW_MS) return;
      const slot = m.get(customerId) ?? { docs: 0, attachments: 0 };
      slot[kind] += 1;
      m.set(customerId, slot);
    }
    for (const d of documents) bump(d.customerId, 'docs', d.deletedAt);
    for (const a of attachments) bump(a.customerId, 'attachments', a.deletedAt);
    return m;
  }, [customers, documents, attachments]);

  async function restoreCustomer(c: DeletedCustomer) {
    if (!vaultKey || !c.plain) return;
    setBusy(`c:${c.id}`);
    setError(null);
    try {
      const newName = `${c.plain.name || '(unnamed)'} (recovered ${dateStr()})`;
      const newPlain: CustomerPlain = { ...c.plain, name: newName };
      const payload = await encryptJson(newPlain, vaultKey);
      await api.restoreCustomer(c.id, payload, { cascadeChildren });
      await pullAll();
      setSelection(null);
      await reload();
    } catch (err: any) {
      setError(err?.body?.error ?? err?.message ?? 'Restore failed.');
    } finally {
      setBusy(null);
    }
  }

  async function restoreDocument(d: DeletedDocument) {
    if (!vaultKey || !d.plain) return;
    setBusy(`d:${d.id}`);
    setError(null);
    try {
      const newTitle = `${d.plain.title || '(untitled)'} (recovered ${dateStr()})`;
      let newYjs = d.plain.yjsState;
      if (newYjs) {
        // Keep the plaintext title and the yjsState meta.title in sync — on
        // first open after restore, lib/yjsCollab.ts seeds from yjsState and
        // would otherwise revert the sidebar title to the pre-suffix one.
        const ydoc = new Y.Doc();
        Y.applyUpdate(ydoc, fromB64(newYjs));
        ydoc.getMap<unknown>('meta').set('title', newTitle);
        newYjs = toB64(Y.encodeStateAsUpdate(ydoc));
        ydoc.destroy();
      }
      const newPlain: DocumentPlain = { ...d.plain, title: newTitle, yjsState: newYjs };
      const payload = await encryptJson(newPlain, vaultKey);
      await api.restoreDocument(d.id, payload);
      await pullAll();
      setSelection(null);
      await reload();
    } catch (err: any) {
      setError(err?.body?.error ?? err?.message ?? 'Restore failed.');
    } finally {
      setBusy(null);
    }
  }

  async function restoreAttachment(a: DeletedAttachment) {
    if (!vaultKey) return;
    setBusy(`a:${a.id}`);
    setError(null);
    try {
      // Re-decrypt to preserve any title/description fields the list view
      // didn't pull out.
      const meta = await decryptJson<AttachmentMeta>(a.metadata, vaultKey);
      const newMeta = { ...meta, filename: suffixFilename(meta.filename) };
      const metadata = await encryptJson(newMeta, vaultKey);
      await api.restoreAttachment(a.id, metadata);
      await pullAll();
      setSelection(null);
      await reload();
    } catch (err: any) {
      setError(err?.body?.error ?? err?.message ?? 'Restore failed.');
    } finally {
      setBusy(null);
    }
  }

  const hasAny = customers.length + documents.length + attachments.length > 0;

  const selectedCustomer =
    selection?.type === 'customer'
      ? customers.find((c) => c.id === selection.id) ?? null
      : null;
  const selectedDocument =
    selection?.type === 'document'
      ? documents.find((d) => d.id === selection.id) ?? null
      : null;
  const selectedAttachment =
    selection?.type === 'attachment'
      ? attachments.find((a) => a.id === selection.id) ?? null
      : null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
      <div className="card w-full max-w-4xl h-[100dvh] md:h-[80vh] flex flex-col">
        <div className="flex items-center gap-2 border-b border-ink-800 px-4 py-3">
          <FolderX size={18} className="text-dragon-500" />
          <h2 className="font-semibold">Orphanage</h2>
          <span className="text-xs text-ink-500 ml-2">Deleted items, recoverable</span>
          <button onClick={onClose} className="btn-ghost ml-auto !p-1">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 min-h-0 flex flex-col md:flex-row">
          <aside className="w-full md:w-72 max-h-[40%] md:max-h-none border-b md:border-b-0 md:border-r border-ink-800 overflow-y-auto">
            {loading && <p className="p-4 text-xs text-ink-500">Loading…</p>}
            {!loading && !hasAny && (
              <p className="p-6 text-sm text-ink-500 text-center">
                Nothing here — deleted items will appear for restoration.
              </p>
            )}

            {customers.length > 0 && (
              <Section title="Customers" icon={Building2}>
                {customers.map((c) => (
                  <RailRow
                    key={c.id}
                    active={selection?.type === 'customer' && selection.id === c.id}
                    onClick={() => setSelection({ type: 'customer', id: c.id })}
                    title={c.plain?.name ?? '(undecryptable)'}
                    subtitle={`deleted ${formatTime(c.deletedAt)}`}
                  />
                ))}
              </Section>
            )}

            {documents.length > 0 && (
              <Section title="Pages" icon={FileText}>
                {documents.map((d) => (
                  <RailRow
                    key={d.id}
                    active={selection?.type === 'document' && selection.id === d.id}
                    onClick={() => setSelection({ type: 'document', id: d.id })}
                    title={d.plain?.title ?? '(undecryptable)'}
                    subtitle={`deleted ${formatTime(d.deletedAt)}`}
                  />
                ))}
              </Section>
            )}

            {attachments.length > 0 && (
              <Section title="Attachments" icon={Paperclip}>
                {attachments.map((a) => (
                  <RailRow
                    key={a.id}
                    active={selection?.type === 'attachment' && selection.id === a.id}
                    onClick={() => setSelection({ type: 'attachment', id: a.id })}
                    title={a.meta?.filename ?? '(undecryptable)'}
                    subtitle={`deleted ${formatTime(a.deletedAt)}`}
                  />
                ))}
              </Section>
            )}
          </aside>

          <section className="flex-1 min-w-0 overflow-y-auto">
            {!selection && (
              <p className="p-6 text-sm text-ink-500">
                Select an item to preview and restore.
              </p>
            )}

            {selectedCustomer && (
              <CustomerPreview
                customer={selectedCustomer}
                orphanedChildCount={orphanedChildCount.get(selectedCustomer.id) ?? 0}
                cascadeCounts={
                  customerCascadeChildren.get(selectedCustomer.id) ?? {
                    docs: 0,
                    attachments: 0,
                  }
                }
                cascadeChildren={cascadeChildren}
              />
            )}
            {selectedDocument && (
              <DocumentPreviewView doc={selectedDocument} />
            )}
            {selectedAttachment && (
              <AttachmentPreview attachment={selectedAttachment} />
            )}
          </section>
        </div>

        {error && (
          <div className="border-t border-red-900 bg-red-950/40 px-4 py-2 text-xs text-red-300">
            {error}
          </div>
        )}

        {selection && canAdminister && (
          <div className="flex items-center gap-2 border-t border-ink-800 px-4 py-3">
            {selectedCustomer?.plain && (
              <>
                {(() => {
                  const counts =
                    customerCascadeChildren.get(selectedCustomer.id) ?? {
                      docs: 0,
                      attachments: 0,
                    };
                  if (counts.docs + counts.attachments === 0) return null;
                  return (
                    <label className="flex items-center gap-2 text-xs text-ink-300">
                      <input
                        type="checkbox"
                        checked={cascadeChildren}
                        onChange={(e) => setCascadeChildren(e.target.checked)}
                        disabled={busy === `c:${selectedCustomer.id}`}
                      />
                      Also restore {counts.docs} page
                      {counts.docs === 1 ? '' : 's'} + {counts.attachments} attachment
                      {counts.attachments === 1 ? '' : 's'}
                    </label>
                  );
                })()}
                <RestoreBar
                  label={`"${selectedCustomer.plain.name || '(unnamed)'} (recovered ${dateStr()})"`}
                  busy={busy === `c:${selectedCustomer.id}`}
                  onClick={() => void restoreCustomer(selectedCustomer)}
                />
              </>
            )}
            {selectedDocument?.plain && (
              <RestoreBar
                label={`"${selectedDocument.plain.title || '(untitled)'} (recovered ${dateStr()})"`}
                busy={busy === `d:${selectedDocument.id}`}
                onClick={() => void restoreDocument(selectedDocument)}
              />
            )}
            {selectedAttachment?.meta && (
              <RestoreBar
                label={`"${suffixFilename(selectedAttachment.meta.filename)}"`}
                busy={busy === `a:${selectedAttachment.id}`}
                onClick={() => void restoreAttachment(selectedAttachment)}
              />
            )}
            {(selectedCustomer && !selectedCustomer.plain) ||
            (selectedDocument && !selectedDocument.plain) ||
            (selectedAttachment && !selectedAttachment.meta) ? (
              <span className="text-xs text-red-300 ml-auto">
                Can't restore — item is undecryptable with the current vault key.
              </span>
            ) : null}
          </div>
        )}
        {selection && !canAdminister && (
          <div className="border-t border-ink-800 px-4 py-3 text-xs text-ink-400">
            You can view soft-deleted items here, but only a vault admin can restore them.
          </div>
        )}
      </div>
    </div>
  );
}

function RestoreBar({
  label,
  busy,
  onClick,
}: {
  label: string;
  busy: boolean;
  onClick: () => void;
}) {
  return (
    <>
      <span className="text-xs text-ink-500 truncate">
        Restore as {label}
      </span>
      <button className="btn-primary ml-auto" disabled={busy} onClick={onClick}>
        <Undo2 size={14} className="mr-1" />
        {busy ? 'Restoring…' : 'Restore'}
      </button>
    </>
  );
}

function Section({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: typeof Building2;
  children: React.ReactNode;
}) {
  return (
    <div className="border-b border-ink-800">
      <h3 className="px-3 pt-3 pb-1 text-xs uppercase tracking-wider text-ink-400 flex items-center gap-1">
        <Icon size={12} /> {title}
      </h3>
      <ul>{children}</ul>
    </div>
  );
}

function RailRow({
  active,
  onClick,
  title,
  subtitle,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  subtitle: string;
}) {
  const cls = active ? 'bg-ink-800' : 'hover:bg-ink-800/60';
  return (
    <li>
      <button
        onClick={onClick}
        className={`w-full text-left px-3 py-2 border-b border-ink-800/60 ${cls}`}
      >
        <div className="flex items-center gap-2">
          <Trash2 size={12} className="text-ink-500 flex-shrink-0" />
          <span className="text-sm truncate">{title}</span>
        </div>
        <div className="text-xs text-ink-500 mt-0.5 truncate">{subtitle}</div>
      </button>
    </li>
  );
}

function CustomerPreview({
  customer,
  orphanedChildCount,
  cascadeCounts,
  cascadeChildren,
}: {
  customer: DeletedCustomer;
  orphanedChildCount: number;
  cascadeCounts: { docs: number; attachments: number };
  cascadeChildren: boolean;
}) {
  const plain = customer.plain;
  if (!plain) {
    return (
      <p className="p-6 text-sm text-ink-500">
        This customer could not be decrypted with the current vault key.
      </p>
    );
  }
  return (
    <article className="p-6">
      <div className="flex items-center gap-3 mb-1">
        {plain.color && (
          <span
            className="inline-block w-4 h-4 rounded-full border border-ink-700"
            style={{ backgroundColor: plain.color }}
            aria-hidden
          />
        )}
        <h3 className="text-lg font-semibold truncate">
          {plain.name || '(unnamed)'}
        </h3>
      </div>
      <p className="text-xs text-ink-500 mb-4">
        deleted {formatTime(customer.deletedAt)} · created {formatTime(customer.createdAt)}
      </p>
      {plain.tags && plain.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-4">
          {plain.tags.map((t) => (
            <span
              key={t}
              className="text-[11px] px-1.5 py-0.5 rounded bg-ink-800 border border-ink-700"
            >
              {t}
            </span>
          ))}
        </div>
      )}
      {(() => {
        const cascadeTotal = cascadeCounts.docs + cascadeCounts.attachments;
        const orphansOutsideCascade = Math.max(0, orphanedChildCount - cascadeCounts.docs);
        if (cascadeTotal > 0 && cascadeChildren) {
          return (
            <p className="text-sm text-emerald-300 bg-emerald-950/30 border border-emerald-900 rounded px-3 py-2">
              Restore will also re-surface {cascadeCounts.docs} page
              {cascadeCounts.docs === 1 ? '' : 's'} and {cascadeCounts.attachments} attachment
              {cascadeCounts.attachments === 1 ? '' : 's'} that went down in the same cascade.
              {orphansOutsideCascade > 0 && (
                <>
                  {' '}
                  {orphansOutsideCascade} page
                  {orphansOutsideCascade === 1 ? '' : 's'} deleted separately will stay in the
                  orphanage.
                </>
              )}
            </p>
          );
        }
        if (cascadeTotal > 0 && !cascadeChildren) {
          return (
            <p className="text-sm text-amber-300 bg-amber-950/30 border border-amber-900 rounded px-3 py-2">
              {cascadeCounts.docs} page{cascadeCounts.docs === 1 ? '' : 's'} and{' '}
              {cascadeCounts.attachments} attachment
              {cascadeCounts.attachments === 1 ? '' : 's'} went down with this customer and
              will stay in the orphanage — tick the "Also restore" option below to bring them
              back together.
            </p>
          );
        }
        if (orphanedChildCount > 0) {
          return (
            <p className="text-sm text-amber-300 bg-amber-950/30 border border-amber-900 rounded px-3 py-2">
              {orphanedChildCount} of this customer's page
              {orphanedChildCount === 1 ? '' : 's'} remain deleted — restore them separately
              from the Pages section.
            </p>
          );
        }
        return null;
      })()}
    </article>
  );
}

function DocumentPreviewView({ doc }: { doc: DeletedDocument }) {
  if (!doc.plain) {
    return (
      <p className="p-6 text-sm text-ink-500">
        This page could not be decrypted with the current vault key.
      </p>
    );
  }
  return (
    <article className="p-6">
      <h3 className="text-lg font-semibold mb-1">
        {doc.plain.title || '(untitled)'}
      </h3>
      <p className="text-xs text-ink-500 mb-4">
        deleted {formatTime(doc.deletedAt)}
        {doc.plain.tags && doc.plain.tags.length > 0 ? ` · tags: ${doc.plain.tags.join(', ')}` : ''}
      </p>
      <DocumentPreviewPane plain={doc.plain} />
    </article>
  );
}

function AttachmentPreview({ attachment }: { attachment: DeletedAttachment }) {
  const vaultKey = useVault((s) => s.vaultKey);
  const [url, setUrl] = useState<string | null>(null);
  const [thumbErr, setThumbErr] = useState(false);

  const meta = attachment.meta;
  const category = meta ? categoryFor(meta.mimetype, meta.filename) : null;
  const isImage = category === 'image';

  useEffect(() => {
    let cancelled = false;
    if (!vaultKey || !isImage) return;
    (async () => {
      try {
        const r = await acquireAttachmentUrl(attachment.id, vaultKey);
        if (cancelled) {
          releaseAttachmentUrl(attachment.id);
          return;
        }
        setUrl(r.url);
      } catch {
        if (!cancelled) setThumbErr(true);
      }
    })();
    return () => {
      cancelled = true;
      if (isImage) releaseAttachmentUrl(attachment.id);
    };
  }, [attachment.id, vaultKey, isImage]);

  if (!meta) {
    return (
      <p className="p-6 text-sm text-ink-500">
        This attachment could not be decrypted with the current vault key.
      </p>
    );
  }
  const Icon = category ? iconFor(category) : Paperclip;
  return (
    <article className="p-6">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={16} className="text-dragon-500" />
        <h3 className="text-lg font-semibold truncate" title={meta.filename}>
          {meta.filename}
        </h3>
      </div>
      <p className="text-xs text-ink-500 mb-4">
        {meta.mimetype} · {formatSize(attachment.size)} · deleted{' '}
        {formatTime(attachment.deletedAt)}
      </p>
      {isImage && (
        <div className="rounded overflow-hidden border border-ink-800 bg-ink-950 max-w-md">
          {url ? (
            <img src={url} alt={meta.filename} className="w-full h-auto block" />
          ) : thumbErr ? (
            <div className="p-6 text-xs text-ink-500">Failed to load preview.</div>
          ) : (
            <div className="p-6 text-xs text-ink-500">Decrypting…</div>
          )}
        </div>
      )}
      {meta.description && (
        <p className="text-sm text-ink-300 mt-4 whitespace-pre-wrap">{meta.description}</p>
      )}
    </article>
  );
}

async function decryptCustomers(
  rows: CustomerDto[],
  key: CryptoKey,
): Promise<DeletedCustomer[]> {
  const out: DeletedCustomer[] = [];
  for (const c of rows) {
    if (!c.deletedAt) continue;
    let plain: CustomerPlain | null = null;
    try {
      plain = await decryptJson<CustomerPlain>(c.payload, key);
    } catch {
      /* undecryptable */
    }
    out.push({
      id: c.id,
      payload: c.payload,
      plain,
      deletedAt: c.deletedAt,
      createdAt: c.createdAt,
    });
  }
  out.sort((a, b) => b.deletedAt.localeCompare(a.deletedAt));
  return out;
}

async function decryptDocuments(
  rows: DocumentDto[],
  key: CryptoKey,
): Promise<DeletedDocument[]> {
  const out: DeletedDocument[] = [];
  for (const d of rows) {
    if (!d.deletedAt) continue;
    let plain: DocumentPlain | null = null;
    try {
      plain = await decryptJson<DocumentPlain>(d.payload, key);
    } catch {
      /* undecryptable */
    }
    out.push({
      id: d.id,
      customerId: d.customerId,
      payload: d.payload,
      plain,
      deletedAt: d.deletedAt,
    });
  }
  out.sort((a, b) => b.deletedAt.localeCompare(a.deletedAt));
  return out;
}

async function decryptAttachments(
  rows: AttachmentDto[],
  key: CryptoKey,
): Promise<DeletedAttachment[]> {
  const out: DeletedAttachment[] = [];
  for (const a of rows) {
    if (!a.deletedAt) continue;
    let meta: AttachmentMeta | null = null;
    try {
      meta = await decryptJson<AttachmentMeta>(a.metadata, key);
    } catch {
      /* undecryptable */
    }
    out.push({
      id: a.id,
      customerId: a.customerId,
      metadata: a.metadata,
      meta,
      size: a.size,
      deletedAt: a.deletedAt,
    });
  }
  out.sort((a, b) => b.deletedAt.localeCompare(a.deletedAt));
  return out;
}

function dateStr(): string {
  // en-CA produces YYYY-MM-DD in the user's local timezone.
  return new Date().toLocaleDateString('en-CA');
}

function suffixFilename(filename: string): string {
  const dot = filename.lastIndexOf('.');
  const stem = dot > 0 ? filename.slice(0, dot) : filename;
  const ext = dot > 0 ? filename.slice(dot) : '';
  return `${stem} (recovered ${dateStr()})${ext}`;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
